import React, { useEffect, useContext, useRef, Suspense } from "react";
import { Welcome } from "./components/Home.jsx";
import { ThemeProvider } from "@mui/material/styles";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useParams,
  useSearchParams,
  Navigate,
  Outlet,
} from "react-router-dom";
import { AuthProvider } from "./auth/Auth";
import { loadProject } from "./projects/ProjectLazy";
import { BoardRoute } from "./projects/BoardRoute";
import { AuthContext } from "./auth/Auth";
import { getGPUTier } from "detect-gpu";
import { useMattoState } from "./MattoState.ts";
import theme from "./theme";
import { EUCookieAndAnalytics } from "./components/EUCookieAndAnalytics.tsx";
import { AppErrorBoundary } from "./components/AppErrorBoundary.tsx";
import { LazyFallback } from "./components/LazyFallback.tsx";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { loadBoard } from "./threejs/BoardLazy.tsx";
import { RequireWebGL } from "./components/BoardLoadError";
import { isWebGLAvailable } from "./libs/webglSupport";

// Heavy route components load lazily so the entry chunk stays small.
const HomeLayout = lazyWithRetry(() =>
  import("./Home/HomeLayout.tsx").then((m) => ({ default: m.HomeLayout })),
);
const ConceptScheme2 = lazyWithRetry(() =>
  import("./Home/concepts_scheme2/ConceptScheme2.tsx").then((m) => ({
    default: m.ConceptScheme2,
  })),
);
const Show = lazyWithRetry(() =>
  import("./projects/Show").then((m) => ({ default: m.Show })),
);
// Public read-only schedule share view — same module as HomeLayout's lazy
// editor route, so the chunk is shared.
const ScheduleSharePage = lazyWithRetry(
  () => import("./Home/schedules/ScheduleEditorPage"),
);
const StudioXRHome = lazyWithRetry(() =>
  import("./studioxr/studioxr").then((m) => ({ default: m.StudioXRHome })),
);
// Virtual Showrooms, anonymous twin of /home/showrooms/* (its own shell;
// the shell applies the staging/launch gate and bounces members to /home).
const PublicShowroomShell = lazyWithRetry(
  () => import("./Home/showrooms/PublicShowroomShell"),
);
// The gated exports, same as the member routes: the shell above already
// redirects before its Outlet, so this is defence in depth rather than the
// only guard — flattening that layout route must not silently expose them.
const PublicShowroomsDirectory = lazyWithRetry(() =>
  import("./Home/showrooms/ShowroomsDirectoryPage").then((m) => ({
    default: m.ShowroomsDirectoryPageGate,
  })),
);
const PublicShowroomBrandPage = lazyWithRetry(() =>
  import("./Home/showrooms/ShowroomBrandPage").then((m) => ({
    default: m.ShowroomBrandPageGate,
  })),
);
// The admin Showroom editor's live preview tab (gated like the pages above).
const ShowroomPreviewPage = lazyWithRetry(
  () => import("./Home/showrooms/ShowroomPreviewPage"),
);
const Brand = lazyWithRetry(() => import("./studioxr/Brand"));
const Brands = lazyWithRetry(() => import("./studioxr/Brands"));
const Pricing = lazyWithRetry(() => import("./stripe/Pricing"));
const ContactSales = lazyWithRetry(() => import("./contact/ContactSales"));
const ContactSupplier = lazyWithRetry(() => import("./contact/ContactSupplier"));
const SketchupLanding = lazyWithRetry(() => import("./stripe/SketchupLanding"));
const SketchupLandingSplit = lazyWithRetry(() => import("./stripe/SketchupLandingSplit"));
import {
  LegacyProjectEditorRedirect,
  LegacyProjectsRedirect,
  LegacyProjectShowRedirect,
} from "./legacyRedirects.tsx";

import { loggedOutHomeRedirect } from "./Home/homeGateRedirect";
import { MaterialPage } from "./materialPage";
import { McpConnectPage } from "./mcpConnect";
import { SCHEDULE_SHARE_PATH_PREFIX } from "./Home/schedules/scheduleConstants";
import { StartingOptions } from "./Home/StartingOptions.tsx";
import Splash, { StartLoginDs } from "./Splash.tsx";
import IframePage from "./IframePage.tsx";
import { CreditPaymentSuccess } from "./components/credits/CreditPaymentSuccess.tsx";
import { gaPageView, pageView } from "./libs/analyticsManager";
import { useLoginState } from "./studioxr/auth/LoginState";
import { useTeamAnalyticsContext } from "./team/useTeamAnalyticsContext";
import { ImpersonationProvider } from "./auth/ImpersonationContext";
import { FeatureFlagsProvider } from "./featureFlags/FeatureFlagsProvider";
import { ImpersonationBanner } from "./auth/ImpersonationBanner";
import { PastDueBanner } from "./stripe/BillingRecovery";
import { ImpersonatePage } from "./auth/ImpersonatePage";
import { ProductFinderDebugRouteLazy } from "./product_finder_debug/ProductFinderDebugRouteLazy";
import { ClipperCompareRouteLazy } from "./clipper_compare/ClipperCompareRouteLazy";

const ProtectedRoute = ({ isAllowed, children }) => {
  if (!isAllowed) {
    return <Navigate to={"/"} replace />;
  }
  return children ? children : <Outlet />;
};

const HomeGate = () => {
  const { currentUser } = useContext(AuthContext);
  const location = useLocation();
  // Signed out on this page (logout, account deletion, an ended session): the
  // page being left is not a link to come back to after the next sign-in.
  const wasSignedIn = useRef(false);
  if (currentUser) wasSignedIn.current = true;

  if (!currentUser) {
    // Public-share carve-outs (concepts2 + schedules) or the sign-in splash.
    // The decision lives in homeGateRedirect.ts so tests exercise the real
    // logic.
    return (
      <Navigate
        to={loggedOutHomeRedirect(
          location.pathname,
          location.search,
          wasSignedIn.current,
        )}
        replace
      />
    );
  }

  return <HomeLayout />;
};

export const RootRedirect = () => {
  const { currentUser } = useContext(AuthContext);
  const showLogin = useLoginState((state) => state.showLogin);
  const setShowLogin = useLoginState((state) => state.setShowLogin);

  // Detect Firebase password reset links landing on /
  const params = new URLSearchParams(window.location.search);
  const isPasswordReset =
    params.get("mode") === "resetPassword" && params.get("oobCode");

  useEffect(() => {
    if (isPasswordReset) {
      setShowLogin("reset");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't redirect if user is in the middle of signup flow or handling password reset
  if (showLogin === "signup" || isPasswordReset) {
    return <Splash />;
  }

  if (currentUser) {
    // Check for redirectTo in sessionStorage
    const redirectTo = sessionStorage?.getItem("redirectTo");

    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // Logged-in users land on the library. This is the destination for
    // post-signup/login too (the one exception, /starting-options for new
    // desktop signups, lives in SignUp.tsx's completeRedirect).
    return <Navigate to="/home/library" replace />;
  }

  return <Splash />;
};

function usePageViews() {
  let location = useLocation();
  const loadedPath = useRef(location.pathname);
  React.useEffect(() => {
    gaPageView(window.location.pathname);
    pageView();
    // The served <head> describes the URL it was loaded for; after a
    // client-side route change its canonical and og:url are stale.
    if (location.pathname !== loadedPath.current) {
      document.querySelectorAll('link[rel="canonical"], meta[property="og:url"]').forEach((el) => el.remove());
    }
  }, [location]);
}

// Legacy /projects/* redirects live in ./legacyRedirects.tsx (extracted so they
// can be unit-tested without importing this heavy entry point).

const LegacyImageRedirect = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const imageUrl = searchParams.get("imageUrl");

  if (id) {
    return <Navigate to={`/home/concepts2/editor/${id}`} replace />;
  }

  if (imageUrl) {
    return (
      <Navigate
        to={`/home/concepts2/editor/new?imageUrl=${encodeURIComponent(imageUrl)}`}
        replace
      />
    );
  }

  return <Navigate to="/home/concepts2/editor/new" replace />;
};

function SwitchApp() {
  const { currentUser } = useContext(AuthContext);
  const permissions = useLoginState((s) => s.permissions);

  // First, so the first page view after load already carries the active team.
  useTeamAnalyticsContext(currentUser?.uid ?? null, permissions);
  usePageViews();

  // Warm the board-editor chunks (three.js-heavy) once the user is
  // authenticated and the browser is idle, so opening a board doesn't stall on
  // a Suspense fallback. The home routes don't need this: they start loading
  // on render right after login. Best-effort only: raw import() thunks (not
  // lazyWithRetry — a silent prefetch failure must never reload the page),
  // every error swallowed, skipped on constrained connections.
  useEffect(() => {
    if (!currentUser) return;
    const conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || "")))
      return;
    // Nothing to warm for a client that will never mount the Canvas.
    if (!isWebGLAvailable()) return;
    const warm = () => {
      loadBoard().catch(() => {});
      loadProject().catch(() => {});
    };
    if (typeof window.requestIdleCallback === "function")
      window.requestIdleCallback(warm, { timeout: 3000 });
    else setTimeout(warm, 1500);
  }, [currentUser]);

  return (
    <>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact-sales" element={<ContactSales />} />
        <Route path="/contact-supplier" element={<ContactSupplier />} />
        <Route path="/for-sketchup" element={<SketchupLanding />} />
        <Route path="/for-sketchup2" element={<SketchupLandingSplit />} />
        {/* Board editor. Public on purpose: shared boards, new_from copies and
            the mobile → /show handoff are reached logged-out; LoadScene enforces
            access itself and bounces non-owners into the copy flow. */}
        <Route path="/board/" element={<Navigate to="/board/new" replace />} />
        <Route
          path="/board/:id"
          element={
            <RequireWebGL>
              <BoardRoute />
            </RequireWebGL>
          }
        />
        {/* LEGACY: the editor lived at /project/:id. ~500k external links —
            redirects stay forever. */}
        <Route path="/project/" element={<LegacyProjectEditorRedirect />} />
        <Route path="/project/:id" element={<LegacyProjectEditorRedirect />} />
        <Route
          path="/show/:id"
          element={
            <RequireWebGL>
              <Show />
            </RequireWebGL>
          }
        />
        <Route
          path="/home/concepts2/share/:conceptId"
          element={<ConceptScheme2 />}
        />
        {/* Public read-only schedule view (is_public docs). Same page as the
            editor route; share behavior derives from the pathname. */}
        <Route
          path={`${SCHEDULE_SHARE_PATH_PREFIX}:id`}
          element={<ScheduleSharePage />}
        />
        {/* Id-less share URL: would otherwise fall through to the Home shell's
            schedules/:id (id "share") and stack the share chrome's Bar under it. */}
        <Route
          path={SCHEDULE_SHARE_PATH_PREFIX}
          element={<Navigate to="/home/schedules" replace />}
        />
        {/* Material detail page — one canonical URL for all visitors (Home
            shell when logged in, public chrome when logged out). Top-level on
            purpose: it must never reach HomeGate, whose logged-out redirect
            drops both the destination and the utm query string. */}
        <Route path="/materials/:id/:slug?" element={<MaterialPage />} />
        {/* WorkOS Standalone Connect Login URI for the MCP server — must
            stay reachable logged-out (the page handles its own auth). */}
        <Route path="/mcp-connect" element={<McpConnectPage />} />
        {/* Public Virtual Showrooms. HomeGate rewrites logged-out
            /home/showrooms hits here (homeGateRedirect.ts). */}
        <Route path="/showrooms" element={<PublicShowroomShell />}>
          <Route index element={<PublicShowroomsDirectory />} />
          <Route path=":slug" element={<PublicShowroomBrandPage />} />
        </Route>
        {/* Outside the /showrooms shell: no brand-slug clash, and no
            signed-in redirect to /home. */}
        <Route
          path="/showroom-preview/:supplierId"
          element={<ShowroomPreviewPage />}
        />
        {/* Share link: the saved draft at showrooms/{id}/previews/{token}. */}
        <Route
          path="/showroom-preview/:supplierId/:token"
          element={<ShowroomPreviewPage />}
        />
        {/* #3544: canonical short path for the dashboard. The page lives inside
            the /home shell so it gets the sidebar and HomeContext. */}
        <Route
          path="/dashboard"
          element={<Navigate to="/home/dashboard" replace />}
        />
        <Route path="/home/*" element={<HomeGate />} />
        <Route element={<ProtectedRoute isAllowed={!!currentUser} />}>
          <Route path="/impersonate" element={<ImpersonatePage />} />
          <Route path="/image/:id?" element={<LegacyImageRedirect />} />
          <Route
            path="/projects/show/:id"
            element={<LegacyProjectShowRedirect />}
          />
          <Route path="/projects/:tab?" element={<LegacyProjectsRedirect />} />
          <Route path="/credits/success" element={<CreditPaymentSuccess />} />
          <Route path="/starting-options" element={<StartingOptions />} />
          <Route
            path="/product-finder-debug"
            element={<ProductFinderDebugRouteLazy />}
          />
          <Route
            path="/product-finder-debug/:conceptId"
            element={<ProductFinderDebugRouteLazy />}
          />
          <Route
            path="/clipper-compare"
            element={<ClipperCompareRouteLazy />}
          />
        </Route>
        <Route path="/brand" element={<Brand />} />
        <Route path="/brand/:slug" element={<Brand />} />
        <Route path="/brands" element={<Brands />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/invite" element={<StudioXRHome />} />
        <Route path="/founding" element={<StudioXRHome />} />
        <Route
          path="/start_login"
          element={<StudioXRHome startDesigning={true} />}
        />
        <Route path="/start_login_ds" element={<StartLoginDs />} />
        <Route
          path="/designstream-brands"
          element={
            <IframePage
              src="https://blog.mattoboard.com/designstream-brands"
              title="DesignStream for Brands"
            />
          }
        />
        <Route
          path="/designstream-designers"
          element={
            <IframePage
              src="https://blog.mattoboard.com/designstream-designers"
              title="DesignStream for Designers"
            />
          }
        />
        <Route
          path="/designstream-homeowners"
          element={
            <IframePage
              src="https://blog.mattoboard.com/designstream-homeowners"
              title="DesignStream for Homeowners"
            />
          }
        />
        <Route
          path="/designstream-renovators"
          element={
            <IframePage
              src="https://blog.mattoboard.com/designstream-renovators"
              title="DesignStream for Renovators"
            />
          }
        />
        <Route path="/boards" element={<StudioXRHome />} />
        <Route exact path="/design" element={<DesignPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <EUCookieAndAnalytics currentUser={currentUser} />
    </>
  );
}

function App() {
  useEffect(() => {
    window._AutofillCallbackHandler =
      window._AutofillCallbackHandler || function () {};

    let isLocalStorageAvailable = false;
    try {
      // Test localStorage availability
      const test = "__test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      isLocalStorageAvailable = true;
    } catch (error) {
      isLocalStorageAvailable = false;
      console.error("LocalStorage is not available:", error);
    }

    if (isLocalStorageAvailable) {
      localStorage.user_session =
        localStorage.user_session || Math.random().toString();
    }

    const getGPUData = async () => {
      try {
        // gpuData_v2: bumped from gpuData to invalidate stale FALLBACK entries
        // cached on machines (e.g. Apple M5) before the detect-gpu upgrade.
        let gpuData = JSON.parse(
          isLocalStorageAvailable ? (localStorage.gpuData_v2 ?? null) : null,
        );
        if (!gpuData) {
          gpuData = await getGPUTier();
          if (isLocalStorageAvailable)
            localStorage.gpuData_v2 = JSON.stringify(gpuData);
          console.log("Getting GPU Data from API", gpuData);
          //logEvent(analytics, 'gpu', { fps: gpuData?.fps ?? 0 })
          if (window.gtag)
            window.gtag("event", "gpu", { fps: gpuData?.fps ?? 0 });
        }
        console.log("Cached GPU Data is ", gpuData);
        useMattoState.setState({ gpuData: gpuData });
        //if (window.dataLayer) window.dataLayer.push({'fps':gpuData?.fps})
      } catch (error) {
        if (isLocalStorageAvailable) localStorage.removeItem("gpuData_v2");
        console.log("Error getting GPU Data", error);
        useMattoState.setState({ gpuData: { fps: 0, tier: 1 } });
      }
    };
    getGPUData();
  }, []);

  useEffect(() => {
    const allowedOrigins = new Set(["https://blog.mattoboard.com"]);

    const handleMessage = (event) => {
      if (!allowedOrigins.has(event.origin)) return;
      if (event.data?.action !== "start_login") return;
      window.location.href = "/start_login_ds";
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        {/* <AuthProvider uid='3pEkUMP38ySutmxDhiXHyBASbY93'> */}
        <ImpersonationProvider>
          <FeatureFlagsProvider>
            <ImpersonationBanner />
            <Router>
              {/* Failed-renewal banner (issue #3956); app-wide so it also
                  shows on the /board editor routes. */}
              <PastDueBanner />
              <AppErrorBoundary>
                <Suspense fallback={<LazyFallback />}>
                  <SwitchApp />
                </Suspense>
              </AppErrorBoundary>
            </Router>
          </FeatureFlagsProvider>
        </ImpersonationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function DesignPage() {
  return (
    <iframe
      src="https://mailchi.mp/be1c3ef83133/designstream"
      title="Design Stream"
      style={{ width: "100%", height: "100vh", border: "none" }}
    ></iframe>
  );
}

export default App;
